/**
 * Social-cluster panels (PXL): Social (friends/presence/chat/moderation), Guild,
 * Mail. Bind to the real presence-class RPCs. Guild/mail payload shapes are
 * surfaced faithfully via DataView (breadth-first) alongside real action controls.
 */

import { useState } from 'react';

import type { ChatChannel, ReportReason } from '@great-cloud/shared/client';

import { useGame } from '../net-context.js';
import { DataView, Empty, SectionTitle, Stat, Tabs } from '../widgets.js';

const CHANNELS: ChatChannel[] = ['zone', 'party', 'guild', 'whisper', 'global'];

export function SocialPanel(): JSX.Element {
  const { state, conn } = useGame();
  const [tab, setTab] = useState('friends');
  const [addName, setAddName] = useState('');
  const [channel, setChannel] = useState<ChatChannel>('zone');
  const [whisperTo, setWhisperTo] = useState('');
  const [body, setBody] = useState('');
  const [mod, setMod] = useState({ name: '', accountId: '', reason: 'harassment' as ReportReason });

  const friends = state.friends?.friends ?? [];

  return (
    <div>
      <Tabs
        tabs={[
          { id: 'friends', label: `Friends (${friends.length})` },
          { id: 'chat', label: 'Chat' },
          { id: 'mod', label: 'Moderation' },
        ]}
        active={tab}
        onSelect={setTab}
      />

      {tab === 'friends' ? (
        <div className="pxl-list">
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="pxl-input grow"
              placeholder="character name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
            />
            <button
              className="pxl-btn"
              disabled={!addName}
              onClick={() => conn.send('friendRequest', { name: addName })}
            >
              add
            </button>
          </div>
          <button className="pxl-btn ghost" onClick={() => conn.send('friendsRequest', {})}>
            refresh
          </button>
          {friends.length === 0 ? <Empty>No friends yet.</Empty> : null}
          {friends.map((f) => (
            <div className="pxl-row" key={f.accountId}>
              <span
                className="pxl-badge"
                style={{
                  color: f.status === 'online' ? 'var(--pxl-good)' : 'var(--pxl-ink-faint)',
                }}
              >
                ●
              </span>
              <div className="grow">
                <div>{f.name}</div>
                <div className="pxl-faint">
                  {f.relation} · {f.status /* id-ok: 'online' | 'offline' */}{' '}
                  {f.location ? `· ${f.location}` : ''}
                </div>
              </div>
              {f.relation === 'incoming' ? (
                <button
                  className="pxl-btn tiny primary"
                  onClick={() =>
                    conn.send('friendRespond', { fromAccountId: f.accountId, accept: true })
                  }
                >
                  accept
                </button>
              ) : null}
              {f.relation === 'accepted' && f.status === 'online' ? (
                <button
                  className="pxl-btn tiny"
                  title="Play in the same channel (the server picks who moves)"
                  onClick={() => conn.send('joinFriend', { accountId: f.accountId })}
                >
                  join
                </button>
              ) : null}
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('friendRemove', { accountId: f.accountId })}
              >
                remove
              </button>
            </div>
          ))}
          <button
            className="pxl-btn ghost"
            onClick={() => conn.send('presenceSet', { appearOffline: true })}
          >
            appear offline
          </button>
        </div>
      ) : null}

      {tab === 'chat' ? (
        <div className="pxl-list">
          <div className="hud-chatlog" style={{ maxHeight: 220 }}>
            {state.chat.length === 0 ? <span className="pxl-faint">No messages.</span> : null}
            {state.chat.map((m, i) => (
              <div key={i}>
                <span className={`chan-${m.channel}`}>[{m.channel}]</span>{' '}
                <span className="pxl-muted">{m.fromName}</span>: {m.body}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              className="pxl-select"
              value={channel}
              onChange={(e) => setChannel(e.target.value as ChatChannel)}
            >
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            {channel === 'whisper' ? (
              <input
                className="pxl-input"
                style={{ width: 90 }}
                placeholder="to"
                value={whisperTo}
                onChange={(e) => setWhisperTo(e.target.value)}
              />
            ) : null}
            <input
              className="pxl-input grow"
              placeholder="message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && body) {
                  conn.send('chatSend', {
                    channel,
                    body,
                    to: channel === 'whisper' ? whisperTo : undefined,
                  });
                  setBody('');
                }
              }}
            />
          </div>
          <div className="pxl-faint">5 channels: zone, party, guild, whisper, global.</div>
        </div>
      ) : null}

      {tab === 'mod' ? (
        <div className="pxl-list">
          <SectionTitle>Report / mute / block</SectionTitle>
          <input
            className="pxl-input"
            placeholder="player name (report)"
            value={mod.name}
            onChange={(e) => setMod({ ...mod, name: e.target.value })}
          />
          <select
            className="pxl-select"
            value={mod.reason}
            onChange={(e) => setMod({ ...mod, reason: e.target.value as ReportReason })}
          >
            {(['harassment', 'spam', 'hate', 'scam', 'other'] as ReportReason[]).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            className="pxl-btn"
            disabled={!mod.name}
            onClick={() => conn.send('report', { name: mod.name, reason: mod.reason })}
          >
            report
          </button>
          <input
            className="pxl-input"
            placeholder="account id (mute/block)"
            value={mod.accountId}
            onChange={(e) => setMod({ ...mod, accountId: e.target.value })}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="pxl-btn grow"
              disabled={!mod.accountId}
              onClick={() =>
                conn.send('muteSet', { accountId: Number(mod.accountId), muted: true })
              }
            >
              mute
            </button>
            <button
              className="pxl-btn grow"
              disabled={!mod.accountId}
              onClick={() =>
                conn.send('blockSet', { accountId: Number(mod.accountId), blocked: true })
              }
            >
              block
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function GuildPanel(): JSX.Element {
  const { state, conn } = useGame();
  const [create, setCreate] = useState({ name: '', tag: '' });
  const [invite, setInvite] = useState('');
  const [motd, setMotd] = useState('');
  const [chat, setChat] = useState('');
  // Disband is irreversible: the first click arms it, the second (named) click sends.
  const [disbandArmed, setDisbandArmed] = useState(false);

  return (
    <div className="pxl-list">
      <button className="pxl-btn ghost" onClick={() => conn.send('guildRequest', {})}>
        refresh guild
      </button>
      {state.guild ? (
        <DataView value={state.guild} />
      ) : (
        <Empty>Not in a guild — found one below.</Empty>
      )}

      <SectionTitle>Found a guild (Shard fee burned)</SectionTitle>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="pxl-input grow"
          placeholder="name"
          value={create.name}
          onChange={(e) => setCreate({ ...create, name: e.target.value })}
        />
        <input
          className="pxl-input"
          style={{ width: 70 }}
          placeholder="tag"
          value={create.tag}
          onChange={(e) => setCreate({ ...create, tag: e.target.value })}
        />
        <button
          className="pxl-btn primary"
          disabled={!create.name || !create.tag}
          onClick={() => conn.send('guildCreate', { name: create.name, tag: create.tag })}
        >
          found
        </button>
      </div>

      <SectionTitle>Manage</SectionTitle>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="pxl-input grow"
          placeholder="invite by name"
          value={invite}
          onChange={(e) => setInvite(e.target.value)}
        />
        <button
          className="pxl-btn"
          disabled={!invite}
          onClick={() => conn.send('guildInvite', { name: invite })}
        >
          invite
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="pxl-input grow"
          placeholder="message of the day"
          value={motd}
          onChange={(e) => setMotd(e.target.value)}
        />
        <button className="pxl-btn" onClick={() => conn.send('guildSetMotd', { motd })}>
          set MOTD
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="pxl-input grow"
          placeholder="guild chat"
          value={chat}
          onChange={(e) => setChat(e.target.value)}
        />
        <button
          className="pxl-btn"
          disabled={!chat}
          onClick={() => {
            conn.send('chatSend', { channel: 'guild', body: chat });
            setChat('');
          }}
        >
          say
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="pxl-btn grow" onClick={() => conn.send('guildLeave', {})}>
          leave
        </button>
        {disbandArmed ? (
          <>
            <button
              className="pxl-btn grow"
              style={{ color: 'var(--pxl-bad)' }}
              onClick={() => {
                setDisbandArmed(false);
                conn.send('guildDisband', {});
              }}
            >
              yes, disband the guild for everyone
            </button>
            <button className="pxl-btn" onClick={() => setDisbandArmed(false)}>
              keep it
            </button>
          </>
        ) : (
          <button className="pxl-btn grow" onClick={() => setDisbandArmed(true)}>
            disband…
          </button>
        )}
      </div>
    </div>
  );
}

export function MailPanel(): JSX.Element {
  const { state, conn, store } = useGame();
  const [mail, setMail] = useState({ to: '', subject: '', body: '', shards: '' });
  const [mailId, setMailId] = useState('');

  return (
    <div className="pxl-list">
      <button className="pxl-btn ghost" onClick={() => conn.send('mailList', {})}>
        refresh mailbox
      </button>
      {state.mail ? <DataView value={state.mail} /> : <Empty>Mailbox empty.</Empty>}

      <SectionTitle>Compose</SectionTitle>
      <input
        className="pxl-input"
        placeholder="to (character name)"
        value={mail.to}
        onChange={(e) => setMail({ ...mail, to: e.target.value })}
      />
      <input
        className="pxl-input"
        placeholder="subject"
        value={mail.subject}
        onChange={(e) => setMail({ ...mail, subject: e.target.value })}
      />
      <input
        className="pxl-input"
        placeholder="body"
        value={mail.body}
        onChange={(e) => setMail({ ...mail, body: e.target.value })}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="pxl-input"
          type="number"
          style={{ width: 90 }}
          placeholder="◈ shards"
          value={mail.shards}
          onChange={(e) => setMail({ ...mail, shards: e.target.value })}
        />
        <button
          className="pxl-btn primary grow"
          disabled={!mail.to || store.isInFlight('mailSend', Date.now())}
          onClick={() => {
            // One send in flight: a double-click must not post the letter (and its Shards) twice.
            if (!store.beginRequest('mailSend', Date.now())) return;
            conn.send('mailSend', {
              to: mail.to,
              subject: mail.subject,
              body: mail.body,
              shards: mail.shards ? Number(mail.shards) : 0,
            });
          }}
        >
          send
        </button>
      </div>
      <SectionTitle>Claim / return by id</SectionTitle>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="pxl-input grow"
          type="number"
          placeholder="mail id"
          value={mailId}
          onChange={(e) => setMailId(e.target.value)}
        />
        <button
          className="pxl-btn"
          disabled={!mailId}
          onClick={() => conn.send('mailClaim', { mailId: Number(mailId) })}
        >
          claim
        </button>
        <button
          className="pxl-btn"
          disabled={!mailId}
          onClick={() => conn.send('mailReturn', { mailId: Number(mailId) })}
        >
          return
        </button>
      </div>
      <Stat k="Mail" v="carries items + Shards (economic channel)" />
    </div>
  );
}
